// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSystemColor, xParserOptions } from "@synd/XDL/Utilities";
import { Divider } from "@synd/XDL/Utilities/layout";
import { XNGAGE_CUSTOM_WIDGET } from "@synd/XDL/constants";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum sectionFields {
    title = "title",
    description = "description",
    link = "link",
    linkLabel = "linkLabel",
    openInNewWindow = "openInNewWindow",
}

const enum fields {
    sections = "sections",
    dividerColor = "dividerColor",
    textAlignment = "textAlignment",
    linkColor = "linkColor",
}

interface SectionFields {
    fields: {
        [sectionFields.title]: string;
        [sectionFields.description]: string;
        [sectionFields.link]: LinkFieldValue;
        [sectionFields.linkLabel]: string;
        [sectionFields.openInNewWindow]: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sections]: SectionFields[];
        [fields.dividerColor]: string;
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.linkColor]: string;
    };
}

export interface MultiTextWidgetStyles {
    container?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    sectionDescription?: TypographyPresentationProps;
    sectionLink?: LinkPresentationProps;
}

export const multiTextWidgetStyles: MultiTextWidgetStyles = {};

export const MultiTextWidget: FC<OwnProps> = ({
    fields: { sections = [], dividerColor, textAlignment, linkColor },
}) => {
    const styles = multiTextWidgetStyles;
    const sectionStyles = {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            text-align: ${textAlignment};
            align-items: ${textAlignment === "left" ? "start" : textAlignment === "center" ? "center" : "end"};

            > *:first-child {
                margin-bottom: auto;
            }
        `,
    };
    let links: any[] = [];
    if (sections.length) {
        const { links: fieldLinks } = useGetLinks(sections, item => item.fields.link || { type: "Page", value: "" });
        links = fieldLinks;
    }

    const activeSections = sections.filter(section => section.fields.title || section.fields.description);

    return (
        <StyledWrapper {...styles.container}>
            {activeSections.map((section, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <Divider direction="vertical" margin="0" color={dividerColor} flipVerticalOn="sm" />}
                    <StyledWrapper {...sectionStyles}>
                        {section.fields.title && (
                            <Typography {...styles.sectionTitle}>
                                {parse(section.fields.title, xParserOptions)}
                            </Typography>
                        )}
                        {section.fields.description && (
                            <Typography {...styles.sectionDescription}>
                                {parse(section.fields.description, xParserOptions)}
                            </Typography>
                        )}
                        {links[index]?.url && (
                            <Link href={links[index].url} alt="learn more" {...styles.sectionLink} color={linkColor}>
                                {section.fields.linkLabel || links[index].title || translate("Learn More")}
                            </Link>
                        )}
                    </StyledWrapper>
                </React.Fragment>
            ))}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const optionsTab = {
    displayName: "Options",
    sortOrder: 1,
};

const multiTextWidget: WidgetModule = {
    component: MultiTextWidget,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                editorTemplate: "ListField",
                name: fields.sections,
                getDisplay: (item: any) => {
                    const title = item.fields.title;
                    return title.replace(/<[^>]*>/g, "").trim() || "Untitled Section";
                },
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
                fieldDefinitions: [
                    {
                        name: sectionFields.title,
                        editorTemplate: "RichTextField",
                        displayName: "Title",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                    },
                    {
                        name: sectionFields.description,
                        editorTemplate: "RichTextField",
                        displayName: "Description",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: sectionFields.linkLabel,
                        editorTemplate: "TextField",
                        displayName: "Link Label",
                        defaultValue: "",
                    },
                    {
                        name: sectionFields.link,
                        displayName: "Link URL",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: sectionFields.openInNewWindow,
                        editorTemplate: "CheckboxField",
                        displayName: "Open in New Window",
                        defaultValue: false,
                    },
                ],
                validate: value => (value.length > 6 ? "We can add up to 6 sections!" : ""),
            },
            {
                fieldType: "Contextual",
                name: fields.dividerColor,
                editorTemplate: "ColorPickerField",
                displayName: "Divider Color",
                defaultValue: getSystemColor("common.border"),
                tab: optionsTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                displayName: "Text Alignment",
                defaultValue: "center",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: optionsTab,
            },
            {
                fieldType: "Contextual",
                name: fields.linkColor,
                editorTemplate: "ColorPickerField",
                displayName: "Link Color",
                defaultValue: getSystemColor("primary"),
                tab: optionsTab,
            },
        ],
    },
};

export default multiTextWidget;
